import axios from 'axios';
import Vue from 'vue';
import Recordset from './Recordset';
import i18n from '@/providers/i18n';

export default class Client {

  constructor(auth = null) {
    var lang = i18n.i18next.language || 'en';
    this.auth = auth;
    this.base = '/api/' + lang + '/';
    this.mybase = '/api/';
    this.login = '/api/' + lang + '/authentication/jwt/[token]';
    this.lastCount = null;
    this.lastResponse = null;
    this.pagecapacity = 200;
    this.requestCache = {};
    this.cacheKeys = (ENV.CACHED_RESOURCES || '')
      .split(',')
      .map(s => s.trim())
      .filter(s => s);
  }


  _call(method, url, data = {}, canceltoken = false, additional_options = {}) {

    if (url[0] === '/') {
      url = url.substring(1);
    }
    var dataContainsFiles = false;
    for (let value of Object.values(data)) {
      if (typeof value === 'object' && value instanceof File) {
        dataContainsFiles = true;
        break;
      }
    }
    if (dataContainsFiles) {
      var fdata = new FormData();
      for (let key in data) {
        if (typeof data[key] === 'object' && data[key].name) {
          fdata.append(key, data[key], data[key].name.replace(/\s/g, '-'));
        } else {
          fdata.append(key, data[key]);
        }
      }
      data = fdata;
    }

    if (url.includes("sso/aquest/master/")) {
      url = this.mybase + url;
    } else {
      url = this.base + url;
    }

    var options = Object.assign({
      method,
      url,
      data
    }, additional_options);
    if (canceltoken) {
      options.cancelToken = new axios.CancelToken(function executor(c) {
        if (!canceltoken.list) {
          canceltoken.list = [];
        }
        canceltoken.list.push(c);
        if (!canceltoken.function) {
          canceltoken.function = (message) => {
            for (let cc of canceltoken.list) {
              if (typeof cc === 'function') {
                cc(message || 'cancelled');
              }
            }
            canceltoken.cancelled = true;
            canceltoken.list = [];
          };
        }
      });
    }
    if (this.auth) {
      options.headers = {
        'auth-token': this.auth
      }
    }

    return Promise.resolve(axios(options)
      .then(result => {
        if (!result.hasOwnProperty('data')) {
          var errorObj = new Error('No response from ' + result.request.responseURL);
          errorObj.code = 1;
          throw errorObj;
        }
        return result;
      })
    );
  }

  download(method, url, data = {}) {

    return this._call(method, url, data, false, {
        responseType: 'arraybuffer',
      })
      .then(result => {
        var headers = result.headers || {};
        var type = headers['content-type'] || 'text/plain';
        var filename;
        if (headers['content-disposition']) {
          var regMatch = /filename=([^\;]+)/.exec(headers['content-disposition']);
          if (regMatch) {
            filename = regMatch[1];
          }
        }
        if (!filename) {
          filename = 'file.txt';
        }
        var blob = new Blob([result.data], {
          type: type
        });
        if (window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveBlob(blob, filename);
        } else {
          var elem = window.document.createElement('a');
          elem.href = window.URL.createObjectURL(blob);
          elem.download = filename;
          document.body.appendChild(elem);
          elem.click();
          document.body.removeChild(elem);
        }
      });
  }

  call(method, url, data = {}, canceltoken = false) {

    return this._call(method, url, data, canceltoken)
      .then(result => {

        this.lastResponse = Object.assign({}, result); //shallow copy
        this.lastResponse.userTime = new Date();
        var response = result.data;

        if (url.includes("sso/aquest/master/")) {
          return response;
        }

        //Explicit error
        if (response.hasOwnProperty('error')) {
          var error = false;
          if (response.error.hasOwnProperty('message')) {
            var errmessage = response.error.message;
          } else {
            var errmessage = response.error;
          }
          if (typeof errmessage === 'string') {
            error = errmessage;
          } else if (typeof errmessage === 'object') {
            var join = function (obj) {
              if (typeof obj === 'string') {
                return obj;
              }
              var joined = [];
              for (let j in obj) {
                joined[joined.length] = join(obj[j]);
              }
              return joined.join(', ');
            };
            error = join(errmessage);
          }
          if (!error && response.hasOwnProperty('codemsg')) {
            error = response.codemsg;
          }
          var code = response.hasOwnProperty('code') ? response.code : 0;
          var errorObj = new Error(error);
          errorObj.code = Number(code);
          throw errorObj;
        }
        //empty response
        if (!response.hasOwnProperty('data')) {
          var errorObj = new Error('No data on ' + result.request.responseURL);
          errorObj.code = 1;
          throw errorObj;
        }
        if (response.hasOwnProperty('countall')) {
          this.lastCount = response.countall;
        } else {
          this.lastCount = null;
        }
        return response.data;
      });
  }

  get(url, canceltoken) {
    //find cache key if needed
    var cacheKey = false;
    var fullUrl = this.base.rtrim('/') + '/' + url.ltrim('/');
    var matches = fullUrl.match(/^\/?api\/[a-z]{2}\/([^?\/&]*)/);
    if (matches && matches[1] && this.cacheKeys.includes(matches[1])) {
      cacheKey = fullUrl;
    }
    if (this.requestCache.hasOwnProperty(cacheKey)) {
      return new Promise(r => r(this.requestCache[cacheKey]));
    }

    var recordset = new Recordset('unknown', []);
    //get array of promises, resolve them and populate recordset
    return this.openread(url, canceltoken).then(promises => Promise.all(promises)).then(values => {
      for (let set of values) {
        recordset._type = set._type;
        recordset.union(set);
      }
      if (cacheKey) {
        this.requestCache[cacheKey] = recordset;
      }
      return recordset;
    });
  }

  count(url, canceltoken) {
    var params = Client.parseParameters(url);
    params.range = ['0,0'];
    return this.get(Client.combineParameters(url, params))
      .then(() => this.lastCount);
  }

  read(url, callback, canceltoken, wait = 0) {

    return this.openread(url, canceltoken, wait).then(promises => {
      promises.map((promise, index) => promise.then(value => callback(value, index)));
      return Promise.all(promises);
    });
  }

  openread(url, canceltoken, wait = 0) {
    var params = Client.parseParameters(url);
    var limitedCount = false;
    var expectOne = false;
    var rangeDefined = params.hasOwnProperty('range');
    if (rangeDefined && params.range.includes('all')) {
      params.range = ['0,' + this.pagecapacity];
    } else if (rangeDefined && params.range.includes('max')) {
      params.range = ['0,' + this.pagecapacity];
      limitedCount = this.pagecapacity;
    } else if (!rangeDefined && !isNaN(Number(url.substr(-1)))) {
      limitedCount = 1;
      expectOne = true;
    } else if (rangeDefined && params.range.includes('single')) {
      limitedCount = 1;
      expectOne = true;
      delete params.range;
    } else if (rangeDefined) {
      limitedCount = params.range[1];
    }
    return this.call('get', Client.combineParameters(url, params), {}, canceltoken)
      .then(data =>
        Object.keys(data).length ?
        new Recordset(
          Object.keys(data)[0],
          data[Object.keys(data)[0]]
        ) :
        new Recordset(
          '_empty_',
          []
        )
      )
      .then(recordset => {
        var requestedCount = Math.max(
          recordset.length, //API returns countall=false for users/me
          this.lastCount
        );
        if (limitedCount) {
          requestedCount = Math.min(requestedCount, limitedCount);
        }
        var pages = Math.max(1, Math.ceil(requestedCount / this.pagecapacity));

        //load other pages and add them to first page results
        var timeouts = [];
        return new Array(pages).fill(0).map((zero, key) => {
          //first page is already loaded
          if (!key) {
            return Promise.resolve(recordset);
          }
          var pageparams = Object.assign({}, params);
          if (expectOne && pageparams.range) {
            delete pageparams.range;
          } else {
            pageparams.range = [key * this.pagecapacity + ',' + this.pagecapacity];
          }
          //perform another get for just this page
          var generate = () => {
            if (canceltoken && typeof canceltoken === 'object' && canceltoken.cancelled) {
              for (let to of timeouts) {
                clearTimeout(to);
              }
              throw 'Request chain was cancelled';
            } else {
              return this.get(Client.combineParameters(url, pageparams), canceltoken);
            }
          };
          return new Promise(resolve => {
            if (wait) {
              var to = setTimeout(() => resolve(generate()), key * wait);
              timeouts.push(to);
              return to;
            } else {
              return resolve(generate());
            }
          });
        })
      });
  }

  put(url, data, canceltoken) {
    return this.call('put', url, data, canceltoken);
  }

  post(url, data, canceltoken) {
    return this.call('post', url, data, canceltoken);
  }

  delete(url, data, canceltoken) {
    return this.call('delete', url, data, canceltoken);
  }

  setauth(token) {
    this.auth = token;
  }

  static parseParameters(url) {
    var parser = document.createElement('a');
    parser.href = url;
    var search = parser.search;
    if (search.length > 1) {
      var params = {};
      var gets = search
        .substr(1)
        .split('&')
        .filter(function (a) {
          //only those with a value
          return a && a.split('=', 2).length === 2 && a.split('=', 2)[1];
        })
        .map(a => decodeURI(a));
      for (let i in gets) {
        let splits = gets[i].split('='),
          key = splits.shift(),
          val = splits.join('=');
        if (!params.hasOwnProperty(key)) {
          params[key] = [];
        }
        params[key] = params[key].concat(val.split(','));
      }
      return params;
    } else {
      return {};
    }
  }

  static combineParameters(url, params) {
    var parser = document.createElement('a');
    parser.href = url;
    if (Object.keys(params).length) {
      var search = [];
      for (let key in params) {
        search[search.length] = key + '=' + params[key].join(',');
      }
      parser.search = '?' + search.join('&');
    } else {
      parser.search = '';
    }
    return parser.pathname + parser.search;
    //return href without http://domain.com:port
    //return parser.href.substr(parser.protocol.length + 2 + parser.host.length);
  }
}



// WEBPACK FOOTER //
// ./src/providers/api/Client.js